const React = require('react')
const { PageBuildContext } = require('./src/context/PageBuildContext')
const {
  restoreWindowScroll,
  cancelWindowScrollRestore,
  persistWindowScroll,
  readPersistedWindowScroll,
  isAdvanceArticlePath,
  syncScrollRestorationForPath,
  isWindowScrollRestoreActive,
  TOUCH_SCROLL_INTENT_PX,
  touchClientY
} = require('./src/util/restoreWindowScroll')

const getScrollMemory = () => {
  if (typeof window === 'undefined') {
    return {
      positions: Object.create(null),
      frozen: Object.create(null),
      pendingPop: false,
      clickPath: null,
      clickAt: 0,
      currentPath: null
    }
  }
  if (!window.__NPF_SCROLL_MEMORY) {
    window.__NPF_SCROLL_MEMORY = {
      positions: Object.create(null),
      frozen: Object.create(null),
      pendingPop: false,
      clickPath: null,
      clickAt: 0,
      currentPath: null
    }
  }
  if (!window.__NPF_SCROLL_MEMORY.frozen) {
    window.__NPF_SCROLL_MEMORY.frozen = Object.create(null)
  }
  return window.__NPF_SCROLL_MEMORY
}

const withTrailingSlash = pathname => {
  if (!pathname || pathname === '/') {
    return pathname
  }
  return pathname.endsWith('/') ? pathname : `${pathname}/`
}

const snapshotWindowScroll = (pathname, options = {}) => {
  if (typeof window === 'undefined' || !pathname) {
    return
  }
  const memory = getScrollMemory()
  const alt =
    pathname && pathname !== '/'
      ? pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : `${pathname}/`
      : null
  if (
    !options.freeze &&
    (memory.frozen[pathname] || (alt && memory.frozen[alt]))
  ) {
    return
  }
  const y = window.pageYOffset || window.scrollY || 0
  memory.positions[pathname] = y
  persistWindowScroll(pathname, y)
  if (alt) {
    memory.positions[alt] = y
    persistWindowScroll(alt, y)
  }
  if (options.freeze) {
    memory.frozen[pathname] = true
    if (alt) {
      memory.frozen[alt] = true
    }
  }
}

const unfreezePath = pathname => {
  if (!pathname) {
    return
  }
  const memory = getScrollMemory()
  memory.frozen[pathname] = false
  if (pathname !== '/') {
    const alt = pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : `${pathname}/`
    memory.frozen[alt] = false
  }
}

const attachScrollSnapshotListeners = () => {
  if (typeof window === 'undefined' || window.__NPF_SCROLL_SNAPSHOT_ATTACHED) {
    return
  }
  window.__NPF_SCROLL_SNAPSHOT_ATTACHED = true

  const memory = getScrollMemory()
  if (!memory.currentPath) {
    memory.currentPath = window.location.pathname
  }

  window.addEventListener(
    'popstate',
    () => {
      memory.pendingPop = true
      if (isWindowScrollRestoreActive()) {
        return
      }
      const leavingPath = memory.currentPath || window.location.pathname
      snapshotWindowScroll(leavingPath, { freeze: true })
    },
    true
  )

  const snapshotFromInternalLink = event => {
    const target = event.target
    if (!target || typeof target.closest !== 'function') {
      return
    }
    const link = target.closest('a[href]')
    if (!link || link.target === '_blank') {
      return
    }
    try {
      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin) {
        return
      }
      const samePath =
        withTrailingSlash(url.pathname) ===
        withTrailingSlash(window.location.pathname)
      if (samePath) {
        return
      }
      snapshotWindowScroll(window.location.pathname, { freeze: true })
      const memory = getScrollMemory()
      memory.clickPath = window.location.pathname
      memory.clickAt = Date.now()
      // A click is never Back; do not let a prior popstate mark this PUSH as POP.
      memory.pendingPop = false
    }
    catch (_error) {
      return
    }
  }

  // Snapshot before Gatsby tears down the page and clamps scrollY.
  window.addEventListener('pointerdown', snapshotFromInternalLink, true)
  window.addEventListener('touchstart', snapshotFromInternalLink, true)
  window.addEventListener('click', snapshotFromInternalLink, true)

  window.addEventListener(
    'scroll',
    () => {
      if (isWindowScrollRestoreActive()) {
        return
      }
      if (memory.persistTimer) {
        return
      }
      const pathAtScroll = window.location.pathname
      memory.persistTimer = window.setTimeout(() => {
        memory.persistTimer = 0
        if (isWindowScrollRestoreActive()) {
          return
        }
        if (window.location.pathname !== pathAtScroll) {
          return
        }
        snapshotWindowScroll(pathAtScroll)
      }, 200)
    },
    { passive: true }
  )

  const persistForUnload = () => {
    const pathname = window.location.pathname
    if (
      isWindowScrollRestoreActive() &&
      typeof window.__NPF_LAST_SCROLL_RESTORE_Y === 'number'
    ) {
      persistWindowScroll(pathname, window.__NPF_LAST_SCROLL_RESTORE_Y)
      return
    }
    snapshotWindowScroll(pathname)
  }
  window.addEventListener('pagehide', persistForUnload)
  window.addEventListener('beforeunload', persistForUnload)

  const onUserScrollIntent = () => {
    if (isWindowScrollRestoreActive()) {
      return
    }
    // Taps and the Back-swipe that just navigated are not scroll intent.
    if (memory.clickAt && Date.now() - memory.clickAt < 500) {
      return
    }
    unfreezePath(window.location.pathname)
  }
  let touchStartY = null
  window.addEventListener('wheel', onUserScrollIntent, { passive: true })
  window.addEventListener(
    'touchstart',
    event => {
      touchStartY = touchClientY(event)
    },
    { passive: true }
  )
  window.addEventListener(
    'touchmove',
    event => {
      const y = touchClientY(event)
      if (touchStartY == null || y == null) {
        return
      }
      if (Math.abs(y - touchStartY) < TOUCH_SCROLL_INTENT_PX) {
        return
      }
      onUserScrollIntent()
    },
    { passive: true }
  )
}

attachScrollSnapshotListeners()

if (typeof window !== 'undefined') {
  syncScrollRestorationForPath(window.location.pathname)
}

exports.onClientEntry = () => {
  attachScrollSnapshotListeners()
  syncScrollRestorationForPath(window.location.pathname)
}

// Snapshot before the incoming page can clamp window.scrollY, unless a
// same-pathname click already captured the pre-navigation offset.
exports.onPreRouteUpdate = ({ prevLocation }) => {
  if (!prevLocation) {
    return
  }
  const memory = getScrollMemory()
  if (memory.frozen[prevLocation.pathname]) {
    return
  }
  snapshotWindowScroll(prevLocation.pathname)
}

// Window is the scrollport. PUSH/REPLACE pathname changes jump to top;
// POP restores the saved window position. Query-only navigations
// (pagination, search filters) leave scroll to page code.
exports.shouldUpdateScroll = ({
  prevRouterProps,
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  const memory = getScrollMemory()
  if (memory.persistTimer) {
    window.clearTimeout(memory.persistTimer)
    memory.persistTimer = 0
  }
  syncScrollRestorationForPath(location.pathname)
  memory.currentPath = location.pathname
  if (!prevRouterProps) {
    memory.pendingPop = false
    if (isAdvanceArticlePath(location.pathname)) {
      const y = readPersistedWindowScroll(location.pathname)
      if (typeof y === 'number') {
        restoreWindowScroll(y, { stickToTarget: true })
        return false
      }
    }
    cancelWindowScrollRestore()
    return true
  }
  if (prevRouterProps.location.pathname === location.pathname) {
    memory.pendingPop = false
    cancelWindowScrollRestore()
    return false
  }

  const isPush = location.action === 'PUSH' || location.action === 'REPLACE'
  const isPop = !isPush && (location.action === 'POP' || memory.pendingPop)
  memory.pendingPop = false

  if (isPop) {
    const savedPosition = getSavedScrollPosition(location)
    const savedY = Array.isArray(savedPosition) ? savedPosition[1] : null
    const y = memory.positions[location.pathname]
    restoreWindowScroll(typeof y === 'number' ? y : savedY, {
      stickToTarget: true
    })
    return false
  }

  cancelWindowScrollRestore()
  persistWindowScroll(location.pathname, 0)
  memory.positions[location.pathname] = 0
  memory.frozen[location.pathname] = true
  if (location.pathname && location.pathname !== '/') {
    const alt = location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : `${location.pathname}/`
    memory.positions[alt] = 0
    memory.frozen[alt] = true
  }
  // Gatsby's one-shot [0, 0] does not stick on mobile article→article:
  // the previous article's Y is still on the window, then growth pins to
  // the footer. Chase 0 the same way POP chases the saved Y.
  restoreWindowScroll(0, { stickToTarget: true })
  return false
}

exports.wrapPageElement = ({ element, props }) =>
  React.createElement(
    PageBuildContext.Provider,
    { value: props.pageContext || {} },
    element
  )
